import cx from 'classnames';
import React, { useMemo } from 'react';
import { DropdownSurface } from '../../shared/dropdown-surface';
import { renderGroupedOptions } from '../../shared/render-grouped-options';
import type { OptionGroup, OptionProp, OptionsGroup } from '../../shared/normalize-options';

type RenderEmptyPayload = { query: string };

type RenderOptionPayload = {
    option: OptionProp;
    group: OptionGroup;
    index: number;
    isActive: boolean;
    isSelected: boolean;
    props: React.LiHTMLAttributes<HTMLLIElement>;
};

type RenderGroupHeaderPayload = { group: OptionGroup };

type TypeaheadDropdownProps = {
    listboxId: string;
    query: string;
    groups: OptionsGroup;
    flattenedOptions: OptionProp[];
    optionIndexByValue: Map<string, number>;
    activeIndex: number;
    selectedOption?: OptionProp;
    onOptionPointerDown: (option: OptionProp) => (event: React.MouseEvent | React.PointerEvent) => void;
    onOptionHover: (index: number) => void;
    onOptionFocus: (index: number) => void;
    onOptionKeyDown: (option: OptionProp, index: number) => (event: React.KeyboardEvent<HTMLLIElement>) => void;
    renderEmpty?: (payload: RenderEmptyPayload) => React.ReactNode;
    isLoading?: boolean;
    renderLoading?: (payload: RenderEmptyPayload) => React.ReactNode;
    loadingText?: string;
    renderOption?: (payload: RenderOptionPayload) => React.ReactNode;
    renderGroupHeader?: (payload: RenderGroupHeaderPayload) => React.ReactNode;
};

const defaultRenderEmpty = (_: RenderEmptyPayload) => (
    <li className="typeaheadfield-option typeaheadfield-option--empty" role="option" aria-disabled="true">
        Ingen match
    </li>
);

const defaultLoadingText = 'Henter forslag...';

const TypeaheadDropdown: React.FC<TypeaheadDropdownProps> = ({
    listboxId,
    query,
    groups,
    flattenedOptions,
    optionIndexByValue,
    activeIndex,
    selectedOption,
    onOptionPointerDown,
    onOptionHover,
    onOptionFocus,
    onOptionKeyDown,
    renderEmpty = defaultRenderEmpty,
    isLoading = false,
    renderLoading,
    loadingText = defaultLoadingText,
    renderOption,
    renderGroupHeader,
}) => {
    const nodes = useMemo(() => {
        return renderGroupedOptions({
            groups,
            renderEmpty: () => renderEmpty({ query }),
            renderOption: ({ option, key, group, optionIndex }) => {
                const filteredIndex = flattenedOptions.findIndex((item) => item.value === option.value);
                const globalIndex = optionIndexByValue.get(option.value);
                const index = filteredIndex >= 0 ? filteredIndex : typeof globalIndex === 'number' ? globalIndex : optionIndex;
                const optionId = `${listboxId}-option-${index}`;
                const isActive = index === activeIndex;
                const optionMeta = option.meta as { kind?: string } | undefined;
                const isCustomOption = optionMeta?.kind === 'custom';
                const isSelected = !isCustomOption && selectedOption?.value === option.value;

                const optionProps = {
                    id: optionId,
                    role: 'option',
                    tabIndex: -1,
                    'aria-selected': isSelected,
                    'data-active': isActive ? 'true' : undefined,
                    'data-disabled': option.disabled ? 'true' : undefined,
                    className: cx(
                        'typeaheadfield-option',
                        {
                            'typeaheadfield-option--active': isActive,
                            'typeaheadfield-option--selected': isSelected,
                            'typeaheadfield-option--disabled': option.disabled,
                        },
                        'flos-custom-select-item'
                    ),
                    onPointerDown: onOptionPointerDown(option),
                    onMouseEnter: () => {
                        if (option.disabled) return;
                        onOptionHover(index);
                    },
                    onFocus: () => {
                        if (option.disabled) return;
                        onOptionFocus(index);
                    },
                    onKeyDown: onOptionKeyDown(option, index),
                } as React.LiHTMLAttributes<HTMLLIElement>;

                const renderedOption = renderOption ? renderOption({ option, group, index, isActive, isSelected, props: optionProps }) : null;

                if (renderedOption && React.isValidElement(renderedOption)) {
                    return React.cloneElement(renderedOption, {
                        key,
                        ...optionProps,
                        ...renderedOption.props,
                    });
                }

                return (
                    <li key={key} {...optionProps}>
                        {renderedOption ?? option.label}
                    </li>
                );
            },
            renderGroup: ({ group, key, children }) => {
                if (!group.label) {
                    return <React.Fragment key={key}>{children}</React.Fragment>;
                }

                return (
                    <li key={key} role="presentation" className="typeaheadfield-option-group">
                        {renderGroupHeader ? renderGroupHeader({ group }) : <p className="typeaheadfield-option-group-header">{group.label}</p>}
                        <ul role="group" className="typeaheadfield-option-group-list">
                            {children}
                        </ul>
                    </li>
                );
            },
        });
    }, [
        activeIndex,
        flattenedOptions,
        groups,
        listboxId,
        onOptionFocus,
        onOptionHover,
        onOptionKeyDown,
        onOptionPointerDown,
        optionIndexByValue,
        query,
        renderGroupHeader,
        renderOption,
        renderEmpty,
        selectedOption,
    ]);

    const keyedNodes = useMemo(() => {
        return nodes.map((node, index) => {
            if (React.isValidElement(node)) {
                if (node.key != null) {
                    return node;
                }
                return React.cloneElement(node, { key: `option-node-${index}` });
            }
            return <React.Fragment key={`option-node-${index}`}>{node}</React.Fragment>;
        });
    }, [nodes]);

    const emptyNode = useMemo(() => {
        const content = renderEmpty({ query });
        if (React.isValidElement(content)) {
            return React.cloneElement(content, { key: 'empty' });
        }
        return (
            <li key="empty" className="typeaheadfield-option typeaheadfield-option--empty" role="option" aria-disabled="true">
                {content}
            </li>
        );
    }, [query, renderEmpty]);

    const loadingNode = useMemo(() => {
        if (!isLoading) return null;
        const payload = { query };
        const content = renderLoading ? renderLoading(payload) : loadingText;
        if (React.isValidElement(content)) {
            return React.cloneElement(content, { key: 'loading' });
        }
        return (
            <li key="loading" className="typeaheadfield-option typeaheadfield-option--loading" role="option" aria-disabled="true">
                {content}
            </li>
        );
    }, [isLoading, loadingText, query, renderLoading]);

    const hasVisibleOptions = flattenedOptions.length > 0;

    return (
        <div className="typeaheadfield-dropdown" role="presentation">
            <DropdownSurface viewportClassName="typeaheadfield-dropdown-surface" scrollAreaClassName="typeaheadfield-dropdown-scroll-area">
                <ul className="typeaheadfield-option-list" id={listboxId} role="listbox">
                    {hasVisibleOptions ? keyedNodes : loadingNode ?? emptyNode}
                </ul>
            </DropdownSurface>
        </div>
    );
};

export type { RenderEmptyPayload, RenderGroupHeaderPayload, RenderOptionPayload, TypeaheadDropdownProps };
export { TypeaheadDropdown };
